<template>
  <div class="e-circular create-ecircular">
    <TMSHeader
      v-if="showHeader"
      :buttons="buttons"
      :dotOption="dotOption"
      :title="currentECircular.subject"
      :status="currentECircular.status"
    />
    <div class="create-ecircular-form main-content__box">
      <div class="one-row">
        <div
          :class="['category', { 'required-error-highlight': noCategory }]"
        >
          <label :class="{ 'required-flag': canEdit }">{{ $t('eCircular.category') }}</label>
          <HKLSelect
            v-if="canEdit"
            :options="categoryHklOptions"
            :value="currentCategoryId"
            placeholder=""
            :showSearch="true"
            @change="onCategoryChange"
          />
          <span v-else>{{ displayCategory }}</span>
        </div>
        <div
          v-if="!isVendorEcircular"
          :class="['building', { 'required-error-highlight': noBuildings }]"
        >
          <label :class="{ 'required-flag': canEdit }">{{ $t('eCircular.building') }}</label>
          <div :key="timestamp">
            <HKLMultiSelect
              v-if="canEdit"
              class="multi-value"
              :options="buildingHklOptions"
              :value="selectedBuildings"
              placeholder=""
              :disabled="buildingDisabled"
              @change="onBuildingsChange"
            />
            <span v-else>{{ getSelectBuildings() || $t('emptyValue') }}</span>
          </div>
        </div>
        <div :class="canEdit ? 'identifier-edit' : 'identifier'">
          <label>{{ $t('eCircular.no') }}</label>
          <pre>{{ currentECircular.code }}</pre>
        </div>
        <div class="selected-user-type">
          <label>{{ $t('eCircular.userType') }}</label>
          <pre>{{ displaySelectedUserType }}</pre>
        </div>
      </div>
      <div
        class="textarea input-row subject"
        :class="{ 'edit': canEdit, 'noSubject': !currentECircular.subject && checkState.subject }"
      >
        <label :class="{ 'required-flag': canEdit }">{{ $t('eCircular.subject') }}</label>
        <TextField
          v-if="canEdit"
          label=""
          :showColon="false"
          :value="currentECircular.subject"
          :required="true"
          @updateValue="updateSubject"
        />
        <pre v-else>{{ currentECircular.subject }}</pre>
      </div>

      <div class="description input-row">
        <label :class="{ 'required-flag': canEdit && isDescRequired }">{{ $t('description') }}</label>
        <TinymceEditor
          v-if="canEdit"
          v-model="description"
          :invalidInput="checkNoDescription"
          :imgSourceId="currentECircular.imgSourceId"
          :additionalToolbar="['hklBulletBList hklNumberedList', 'image', 'link unlink', 'fontselect fontsizeselect', 'superscript subscript']"
        />
        <ECircularPreviewIndex v-else />
      </div>
      <div :class="['signature input-row', { 'required-error-highlight': noSignature }]">
        <label :class="{ 'required-flag': canEdit }">{{ $t('footer') }}</label>
        <HKLSelect
          v-if="canEdit && signatureTemplatesOptions.length > 1"
          :options="signatureHklOptions"
          :value="currentECircular.signatureId"
          placeholder=""
          @change="onSignatureChange"
        />
        <p
          v-if="!canEdit || signatureTemplatesOptions.length <= 1"
          class="small-6"
        >
          {{ (signatureTemplate || {}).title || $t('emptyValue') }}
        </p>
        <div
          v-if="(signatureTemplate || {}).content && canEdit"
          :key="new Date().getTime()"
          class="clearfix content"
          v-html="(signatureTemplate || {}).content"
        />
      </div>
      <div
        v-if="canEdit || attachmentsLength"
        class="attachments input-row"
      >
        <label>{{ $t('attachments') }}</label>
        <FileUpload
          v-if="canEdit"
          :initLoading="false"
          :attachment.sync="attachments"
          :resourceId="ecircularId"
          :uploadDocTypes="eCircularAttachmentLimits.docUploadTypes"
          :maxNumberOfFiles="eCircularAttachmentLimits.maxFiles"
          :maxTotalFileSizeInBytes="eCircularAttachmentLimits.maxTotalSizeBytes"
          :uploadNoteRows="eCircularAttachmentNoteRows"
          :filesExceedMessage="eCircularFilesExceedMessage"
          :totalSizeExceedMessage="eCircularTotalSizeExceedMessage"
          :useTotalSizeMessageForFileSizeError="true"
        />
        <EditAttachments
          v-else
          :canEdit="false"
          :canDelete="false"
          :sourceId="currentECircular.id"
          :canDownloadAll="true"
          :zipName="`${currentECircular.subject}_eCircular_Documents.zip`"
        />
      </div>
      <div class="date-time">
        <label>{{ $t('eCircular.scheduleDateTime') }}</label>
        <DateTimeSelector
          v-if="canEdit"
          :key="timestamp"
          :timeOptions="scheduleTimeOptions"
          :date="currentECircular.scheduleDate"
          :showClearBtn="true"
          :setMinTime="true"
          :disabledDates="{to: yesterday}"
          disabled
          @updateDate="updateScheduleDate"
          @update:partialSelected="updateTimeOptions"
        />
        <div v-else>
          {{ formatDate(currentECircular.scheduleDate) }}
        </div>
      </div>
    </div>

    <CancelECircular ref="cancelModal" />
    <ActionConfirmModal
      ref="buildingChangedConfirmation"
      :modalId="'buildingChangedConfirmation'"
      :confirmTitle="$t('eCircular.buildingChangedConfirmation')"
    />
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import TinymceEditor from '@/controls/TinymceEditor'

import DateTimeSelector from '@/controls/DateTimeSelector'
import EditAttachments from '@/controls/EditAttachments'
import FileUpload from '@/modules/FileUpload'
import HKLSelect from '@/controls/Select/HKLSelect'
import HKLMultiSelect from '@/controls/Select/HKLMultiSelect'
import TextField from '@/controls/TextField'
import TMSHeader from '@/controls/TMSHeader'
import CancelECircular from './CancelECircular'
import ActionConfirmModal from '@/controls/ActionConfirmModal'

import { templateType } from '@/utils/consts'
import dateUtil from '@/utils/dateUtil'
import i18n from '@/utils/i18n'
import mutationTypes from '@/store/mutation-types'
import notification from '@/utils/notification'
import navigation from '@/utils/navigation'
import * as ecircularAPI from '@/api/ecircular/request'
import * as departmentAPI from '@/api/departments/request'
import { E_CIRCULAR_TYPES, E_CIRCULAR_STATUS, E_CIRCULAR_WORKING_STATUS, E_CIRCULAR_EDITABLE_STATUS } from './consts'
import { getCategoryOptions, formatECircularUserTypeDisplay } from './utils'
import { getTemplatesBySourceId } from '@/api/settings/templates.request'

import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { getTemplates } from '@/api/settings/request'
import { aclHelper, aclPermsType, tmsPermsTypes } from '@/utils/aclHelper'
import { utils as formUtils } from '@/plugin/InputChange'
import httpHeaderMixin from '@/controls/httpHeaderMixin'
import ECircularPreviewIndex from './ECircularViews/ECircularPreviewIndex'
import {
  fetchECircularAttachmentLimits,
  normalizeECircularAttachmentLimits,
  validateECircularAttachmentFiles,
} from './attachmentLimits'

export default {
  name: 'CreateECircular',
  components: {
    CancelECircular,
    TMSHeader,
    TextField,
    HKLSelect,
    HKLMultiSelect,
    FileUpload,
    EditAttachments,
    DateTimeSelector,
    ECircularPreviewIndex,
    TinymceEditor,
    ActionConfirmModal,
  },
  mixins: [httpHeaderMixin],
  props: {
    showHeader: {
      type: Boolean,
      default: true,
    },
    canEdit: {
      type: Boolean,
      default: true,
    },
  },
  data () {
    return {
      attachments: {
        description: '',
        files: [],
      },
      categoryOptions: [],
      allCategories: [],
      buildingOptions: [],
      ecircularId: '',
      yesterday: new Date((new Date()).getTime() - dateUtil.ONE_DAY_MILLI),
      ecricularFiles: [],
      userAddRecipients: null,
      userAddRecipientsOptions: [],
      noCategory: false,
      noBuildings: false,
      noSubject: false,
      nodescription: false,
      noSignature: false,
      vendorSignatureTemplates: [],
      scheduleTimeOptions: [],
      timestamp: '',
      uploadHeaders: '',
      buildingDisabled: true,
      checkState: {
        category: false,
        subject: false,
        buildings: false,
        description: false,
        signature: false,
      },
      userSiteIds: [],
      userId: '',
      allBuildings: [],
      templateByDep: [],
      initAllBuildings: [],
      initECTemplate: [],
      eCircularAttachmentLimits: normalizeECircularAttachmentLimits(),
    }
  },
  computed: {
    ...mapGetters('ecircular', [
      'currentECircular',
    ]),
    ...mapGetters('user', [
      'currentUser',
      'buildings',
    ]),
    description: {
      get: function () {
        return this.currentECircular.description
      },
      set: function (value) {
        this.updateContent(value)
      },
    },
    editBtns () {
      return [{
        title: this.$t('eCircular.saveAsDraft'),
        cls: 'btn--darkblue',
        func: this.save,
      }, {
        title: this.$t('action.submit'),
        cls: 'btn--orange',
        func: this.submit,
      }]
    },
    selectCategory () {
      return this.allCategories.find((option) => {
        return option.categories.some(cate => cate.value.categoryId === this.currentECircular.categoryId)
      })
    },
    checkNoDescription () {
      return this.isDescRequired && !this.currentECircular.description && this.checkState.description
    },
    displayCategory () {
      if (this.selectCategory) {
        const cate = this.selectCategory.categories.find(cate => cate.value.categoryId === this.currentECircular.categoryId)
        return cate.name
      } else if (this.currentECircular.category) {
        return this.currentECircular.category.name
      }
      return ''
    },
    currentCategory () {
      if (this.selectCategory) {
        const cate = this.selectCategory.categories.find(cate => cate.value.categoryId === this.currentECircular.categoryId)
        return cate
      }
      return {}
    },
    currentCategoryId () {
      return this.currentECircular.categoryId || undefined
    },
    categoryHklOptions () {
      return this.categoryOptions.map((dept) => ({
        name: dept.departments || dept.name,
        options: (dept.categories || []).map((cate) => ({
          name: cate.name,
          value: cate.value.categoryId,
        })),
      }))
    },
    buildingHklOptions () {
      return this.buildingOptions.map((code) => ({ name: code, value: code }))
    },
    signatureHklOptions () {
      return this.signatureTemplatesOptions.map(({ id, title }) => ({
        name: title,
        value: id,
      }))
    },
    canCreateUpdate () {
      return aclHelper.getAccess(tmsPermsTypes.tenantEcircular.createUpdate) || aclHelper.getAccess(aclPermsType.vendorECircular.createUpdateSelectSendCancel)
    },
    canCancel () {
      return (aclHelper.getAccess(tmsPermsTypes.tenantEcircular.cancel) || aclHelper.getAccess(aclPermsType.vendorECircular.createUpdateSelectSendCancel)) && Object.values(E_CIRCULAR_WORKING_STATUS).includes(this.currentECircular.status)
    },
    buttons () {
      return this.canCreateUpdate ? this.editBtns : []
    },
    isVendorEcircular () {
      return lodash.get(this.currentECircular, 'type') === E_CIRCULAR_TYPES.VENDOR
    },
    isDescRequired () {
      const hasFooter = this.templateByDep.some(template => template.type === templateType.ECIRCULAR_TENANT_DEPARTMENT_FOOTER && template.content)
      return this.isVendorEcircular || !hasFooter
    },
    dotOption () {
      let options = []
      if (this.canCreateUpdate) {
        options.push({
          title: i18n.t('action.duplicate'),
          operate: this.duplicateECircular,
        })
      }
      if (this.canCancel) {
        options.push({
          title: i18n.t('action.cancel'),
          operate: this.openCancelModal,
        })
      }
      return options
    },
    canSend () {
      return this.currentECircular.status === E_CIRCULAR_STATUS.SUBMITTED &&
        aclHelper.getAccess(tmsPermsTypes.tenantEcircular.send)
    },
    signatureTemplatesOptions () {
      return this.isVendorEcircular ? this.vendorSignatureTemplates : this.tenantSignatureTemplates
    },
    selectedSignature () {
      const id = this.currentECircular.signatureId
      let content = this.currentECircular.signature
      let title = this.currentECircular.signatureTitle

      if (id && (lodash.isNil(title) || lodash.isNil(content))) {
        let templates = []
        this.allBuildings.forEach(building => {
          templates.push(...building.ec_templates)
        })
        const template = templates.find(item => item.id === id)
        if (template) {
          const siteCode = lodash.get(template, 'site.code', null)
          title = title || this.formatTitle(template.title, siteCode)
          content = content || template.content
        }
      }

      return { id, content, title }
    },
    tenantSignatureTemplates () {
      const selectedBuildingDetails = this.allBuildings.filter((building) => {
        return this.selectedBuildings.includes(building.code)
      })
      const templates = selectedBuildingDetails.map((building) => {
        const tenantTemplates = building.ec_templates.filter(template => template.type === templateType.ECIRCULAR_TENANT)
        return tenantTemplates.map(({ id, title, content, site }) => ({ id, title: this.formatTitle(title, lodash.get(site, 'code', null)), content }))
      })
      return lodash.uniqWith(lodash.flattenDeep(templates), lodash.isEqual)
    },
    signatureTemplate () {
      if (this.signatureTemplatesOptions.length === 1 && !this.currentECircular.signatureId) {
        const autoSelected = this.signatureTemplatesOptions[0]
        this.silentUpdateSignatureAttr(autoSelected)
        return autoSelected
      }
      const signatureOptions = this.signatureTemplatesOptions.filter(option =>
        option.zones && option.zones.some(zone => this.currentECircular.buildings.includes(zone)))

      const selectedId = this.currentECircular.signatureId
      const selectedTemplate = signatureOptions.find(option => option.id === selectedId)
      const template = this.selectedSignature || selectedTemplate || signatureOptions[0] || {}
      return template
    },
    selectedBuildings () {
      return this.currentECircular.buildings
    },
    attachmentsLength () {
      return lodash.get(this.currentECircular, 'files', []).length
    },
    displaySelectedUserType () {
      return formatECircularUserTypeDisplay(this.currentECircular.userType)
    },
    eCircularAttachmentNoteRows () {
      return [
        this.$t('hint.eCircularAttachmentLimits', {
          maxFiles: this.eCircularAttachmentLimits.maxFiles,
          maxSize: this.eCircularAttachmentLimits.maxTotalSizeLabel,
        }),
        this.$t('hint.eCircularAttachmentFileTypes', {
          fileTypes: this.eCircularAttachmentLimits.docUploadTypes,
        }),
      ]
    },
    eCircularFilesExceedMessage () {
      return this.$t('error.fileUpload.eCircularFilesExceedLimitation', {
        max: this.eCircularAttachmentLimits.maxFiles,
      })
    },
    eCircularTotalSizeExceedMessage () {
      return this.$t('error.fileUpload.eCircularTotalSizeExceedLimitation', {
        max: this.eCircularAttachmentLimits.maxTotalSizeLabel,
      })
    },
  },
  watch: {
    canEdit (nVal) {
      // init attachments
      if (nVal === true) {
        this.attachments.files = lodash.cloneDeep(this.currentECircular.files)
      }
    },
  },
  async mounted () {
    this.userSiteIds = this.currentUser.locations.map(location => location.id)
    this.userId = this.currentUser.id

    this.uploadHeaders = await this.getHeaders()
    this.ecircularId = this.$route.params.id
    if (this.showHeader) {
      await this.loadECircular(this.ecircularId)
      if (!this.editableCheck()) return
    }
    await this.loadECircularAttachmentLimits()
    const departmentIds = this.currentUser.departments.map(dept => dept.id)
    const results = await departmentAPI.getCategoriesByDepartment({ ids: departmentIds, isAll: true, type: lodash.get(this.currentECircular, 'type') })
    this.allCategories = getCategoryOptions(results, true)
    this.categoryOptions = lodash.cloneDeep(this.allCategories).map(item => {
      item.categories = item.categories.filter(ca => ca.status === true)
      return item
    })
    if (this.currentECircular.categoryId) {
      const cate = lodash.get(this.selectCategory, 'categories', []).find(cate => cate.value.categoryId === this.currentECircular.categoryId)
      const departmentId = cate.value.departmentId
      await this.departmentRelatedData(departmentId)
      this.initAllBuildings = lodash.cloneDeep(this.allBuildings)
      this.initECTemplate = lodash.cloneDeep(this.templateByDep)
    }
    await this.setupVendorSignatures()
    this.attachments.files = lodash.cloneDeep(this.currentECircular.files)
    this.scheduleTimeOptions = dateUtil.getTimeOptions(this.currentECircular.scheduleDate || new Date())
    this.timestamp = new Date().getTime()
  },
  methods: {
    ...mapActions('ecircular', [
      'updateECircularById',
      'loadECircular',
      'updateECircular',
      'updateECircularAllInfo',
      'createECircular',
    ]),
    ...mapMutations('ecircular', {
      updateECircularOneAttribute: mutationTypes.ECIRCULAR_UPDATE_ONE_ATTRIBUTE,
    }),
    formatDate (date, showTime = true) {
      return date ? dateUtil.formatDate(date, showTime) : this.$t('emptyValue')
    },
    getSelectBuildings () {
      return lodash.get(this.currentECircular, 'buildings', []).join(',')
    },
    async updateBuildings () {
      const value = this.selectedBuildings
      this.updateAttribute('buildings', value)
      await this.updateECircular({
        id: this.currentECircular.id,
        name: 'buildings',
        value,
      })
      formUtils.resetDirty()
    },
    async loadECircularAttachmentLimits () {
      this.eCircularAttachmentLimits = await fetchECircularAttachmentLimits()
    },
    checkAttachmentLimits (files) {
      const validationResult = validateECircularAttachmentFiles(files || [], [], this.eCircularAttachmentLimits)
      if (validationResult.valid) return false

      if (validationResult.reason === 'files') {
        notification.fireNotification(false, this.eCircularFilesExceedMessage)
      } else {
        notification.fireNotification(false, this.eCircularTotalSizeExceedMessage)
      }
      return true
    },
    async save () {
      this.updateAttribute('files', this.attachments.files)
      await this.updateECircularAllInfo()
      notification.fireNotification(true, this.$t('success'))
      this.buildingDisabled = true
      formUtils.resetDirty()
    },
    validation () {
      this.noCategory = false
      this.noBuildings = false
      this.noSubject = false
      this.nodescription = false
      this.noSignature = false
      this.checkState = {
        category: true,
        subject: true,
        buildings: true,
        description: true,
        signature: true,
      }
      let emptySignature = false
      if (!this.currentCategory.value) {
        this.noCategory = true
      }
      if (!this.currentECircular.subject) {
        this.noSubject = true
      }
      if (this.isDescRequired && !this.currentECircular.description) {
        this.nodescription = true
      }
      if (!this.currentECircular.signatureId) {
        this.noSignature = true
        notification.error(this.$t('hint.emptySignature'))
      }
      if (!this.currentECircular.buildings.length && !this.isVendorEcircular) {
        this.noBuildings = true
      }
      if (this.isVendorEcircular && Object.keys(this.signatureTemplate).length === 0) {
        emptySignature = true
      }
      fusionInputCheckUtil.validateAllFields({allowEmpty: 'all'}, '.create-ecircular-form')
      const isFormValid = fusionInputCheckUtil.isAllFieldsValid('.create-ecircular-form')
      return isFormValid && !this.noCategory && !this.noSubject && !this.nodescription && !this.noSignature && !this.noBuildings && !emptySignature
    },
    async doSubmit () {
      const isAttachmentInvalid = this.checkAttachmentLimits(this.attachments.files)
      const isValid = this.validation()
      if (!isValid || isAttachmentInvalid) {
        return
      }
      await this.save()
      await ecircularAPI.updateECircularStatus(this.currentECircular.id, E_CIRCULAR_STATUS.SUBMITTED)
      formUtils.resetDirty()

      this.$router.replace({
        name: 'eCircularDetail',
        params: {
          id: this.currentECircular.id,
        },
      })
    },
    async departmentRelatedData (departmentId) {
      this.allBuildings = await departmentAPI.getBuildingsByDepartment(departmentId, { userId: this.userId, userSiteIds: this.userSiteIds })
      this.buildingOptions = this.allBuildings.map(building => building.code)
      this.buildingDisabled = false
      this.templateByDep = await getTemplatesBySourceId(departmentId)
    },
    async submit () {
      let ecircularBuilding = this.currentECircular.buildings[0]
      if (ecircularBuilding && !this.buildings.some(b => b === ecircularBuilding)) {
        this.$refs.buildingChangedConfirmation.open(this.doSubmit)
      } else {
        this.doSubmit()
      }
    },
    async duplicateECircular () {
      await this.createECircular({id: this.currentECircular.id})
    },
    updateAttribute (name, value, setDirty = true) {
      this.updateECircularOneAttribute({name, value})
      if (setDirty) {
        formUtils.setDirty()
      }
    },
    updateSubject (val) {
      this.updateAttribute('subject', val)
      this.checkState.subject = false
    },
    silentUpdateSignatureAttr ({id, title, content}) {
      this.updateAttribute('signatureId', id, false)
      this.updateAttribute('signatureTitle', title, false)
      this.updateAttribute('signature', content, false)
    },
    updateSignatureAttr (option) {
      this.updateAttribute('signatureId', option.id)
      this.updateAttribute('signatureTitle', option.title)
      this.updateAttribute('signature', option.content)
    },
    onCategoryChange (categoryId) {
      if (!categoryId) return
      for (const dept of this.allCategories) {
        const cate = dept.categories.find((item) => item.value.categoryId === categoryId)
        if (cate) {
          this.updateCategoryAttr(cate)
          return
        }
      }
    },
    onBuildingsChange (values) {
      const buildings = Array.isArray(values) ? values : []
      this.updateAttribute('buildings', buildings)
      this.checkState.buildings = false
      this.checkState.signature = false
      if (!this.signatureTemplatesOptions.some((option) => option.id === this.currentECircular.signatureId)) {
        this.resetSignatureAttr()
      }
      formUtils.setDirty()
    },
    onSignatureChange (id) {
      const option = this.signatureTemplatesOptions.find((item) => item.id === id)
      if (option) {
        this.updateSignatureAttr(option)
      }
    },
    resetSignatureAttr () {
      this.updateAttribute('signature', null)
      this.updateAttribute('signatureId', null)
      this.updateAttribute('signatureTitle', null)
    },
    async updateCategoryAttr (option) {
      if (this.currentECircular.categoryId === option.value.categoryId) return
      const departmentId = option.value.departmentId
      await this.departmentRelatedData(departmentId)

      this.checkState.signature = false
      this.checkState.buildings = false
      this.updateAttribute('buildings', [])
      this.updateAttribute('categoryShortname', option.value.shortname)
      this.updateAttribute('categoryId', option.value.categoryId)
      this.resetSignatureAttr()
      formUtils.setDirty()
    },
    resetData () {
      this.allBuildings = lodash.cloneDeep(this.initAllBuildings)
      this.templateByDep = lodash.cloneDeep(this.initECTemplate)
    },
    category_options () {
      const key = []
      this.categoryOptions.map(item => {
        key.push(item.name)
      })
      return key
    },
    updateScheduleDate (date) {
      if (date !== this.currentECircular.scheduleDate) {
        this.updateAttribute('scheduleDate', date)
        this.scheduleTimeOptions = dateUtil.getTimeOptions(date || new Date())
      }
    },
    updateTimeOptions ([selectedDate, selectedTime]) {
      if (selectedDate && !selectedTime) {
        const date = new Date(dateUtil.parse(selectedDate))
        this.scheduleTimeOptions = dateUtil.getTimeOptions(date)
      }
    },
    updateUserAddRecipients (who) {
      this.userAddRecipients = who
      this.updateAttribute('managedBy', who.id)
    },
    async getHeaders () {
      let headers = {
        'Accept': 'application/json',
        'Cache-Control': 'no-cache',
        'X-Requested-With': 'XMLHttpRequest',
        'X-Content-Type-Options': 'nosniff',
      }
      return this.getHttpHeaders(headers)
    },
    openCancelModal () {
      this.$refs['cancelModal'].open()
    },
    updateContent (html) {
      this.updateAttribute('description', html, (this.currentECircular.description || '') !== html)
      this.checkState.description = false
    },
    editableCheck () {
      if (!E_CIRCULAR_EDITABLE_STATUS[this.currentECircular.status]) {
        navigation.replacePage('eCircularDetail', this.currentECircular.id)
        return false
      }
      return true
    },
    async setupVendorSignatures () {
      if (this.isVendorEcircular) {
        const templates = await getTemplates(templateType.ECIRCULAR_VENDOR)
        const formatedTemplates = templates.map(({ id, title, content, zones }) => ({ id, title, content, zones }))
        this.vendorSignatureTemplates = formatedTemplates
      }
    },
    formatTitle (title, site) {
      return site ? `${site} - ${title}` : title
    },
  },
}
</script>

<style lang='scss'>
@import '~styles/e-circular/styles';

.create-ecircular {
  .tms-header {
    padding-left: 15px;
    padding-right: 15px;
  }
  .tms-header .content{
    .main-title {
      font-family: Georgia;
      font-size: 20px !important;
      font-weight: bold;
    }
    .tenant-project-status-rejected {
      border:1px solid #FF363E !important;
      color: #FF363E !important;
    }
    .tenant-project-status-submitted {
      border:1px solid #F7AA51 !important;
      color: #F7AA51 !important;
    }
  }
  .ql-container {
    height: 400px;
  }
  label {
    font-family: Verdana;
    font-size: 14px;
    font-weight: bold;
    font-style: normal;
    color: #0C3A58;
    margin-bottom: 5px;
  }
  .create-ecircular-form {
    margin: 15px auto;
    padding: 15px;
    background: white;
    box-shadow: 0 2px 4px 0 rgba(212, 212, 212, 0.5);
    .input-row {
      margin-bottom: 10px;
    }
    .input-row.subject.edit {
      margin-top: 15px;
    }
    .one-row {
      .identifier {
        display: inline-block;
        vertical-align: top;
        margin-right: 50px;
        height: 70px;
      }
      .building {
        display: inline-block;
        vertical-align: top;
        max-width: calc(100% - 570px);
        margin-right: 50px;

        .hkl-select.multi-value {
          min-height: 38px;
          height: auto;

          .ant-select-selection {
            overflow-y: auto;
            height: auto;
            min-height: 38px;
            max-height: none;
            width: 100%;
          }
          .ant-select-selection__rendered {
            max-height: none;
            line-height: normal;
          }
        }
      }
      .identifier-edit, .category {
        display: inline-block;
        vertical-align: top;
        height: 70px;
      }
      .identifier-edit {
        height: 70px;
        margin-right: 30px;
        width: 130px;
        pre {
          height: 40px;
          line-height: 40px
        }
      }
      .selected-user-type {
        display: inline-block;
        vertical-align: top;
        margin-right: 50px;
        height: 70px;
        max-width: 320px;
        pre {
          margin: 0;
          white-space: pre-wrap;
          word-break: break-word;
          font-family: inherit;
        }
      }
      .category {
        margin-right: 50px;
        .hkl-select {
          width: 300px;
        }
      }
    }
    .noSubject {
      textarea {
        background: #f9ecea;
      }
    }
    .content {
      border: dotted 1px #B5B5B5;
      min-height: 60px;
      padding: 10px;
      margin-top: 10px;
      display: flex;
      flex-direction: column;
    }
  }
  .category.input-row {
    ::v-deep .hkl-select {
      width: 30%;
      max-width: 260px;
    }
  }

  .signature.input-row {
    ::v-deep .hkl-select {
      width: 40%;
      max-width: 500px;
    }
  }
  .date-time {
    margin-top: 10px;
  }
  .date-time-select {
    min-width: 300px;
    width: 50%;
    ::v-deep .hkl-select {
      width: 120px;
      min-width: 120px;
    }
  }
}
.choose-user-add-recipients-modal {
  .modal-title {
    font-weight: bold;
  }
}
</style>
